import React, { useState, forwardRef, useImperativeHandle, useRef, useEffect, useCallback } from 'react'
import { message, Modal } from 'antd'
import zyTrack from 'zy-track/src'
import Icon from './Icon'
import UploadImg from './UploadImg'
import StructuredDescEditor, { generateInsertFunction } from './StructuredDescEditor'
import { TAB_OPTIONS, MAX_REF_IMGS, IMG_LABELS, TEMPLATE_DATA, DEFAULT_MODEL, DEFAULT_QUALITY, DEFAULT_RATIO, FDAI_MESSAGE_SOURCE, normalizeGenerateParams } from './constants'
import { apiCreateConversation, apiSendMessage, apiFetchMessages, apiFetchHistory, apiSingleDownload, apiAddUploadPicLog, apiDeleteConversation, apiConversationGet, apiGetTaskCostCount } from './service'
import { downloadBlob, downloadOssImageByUrl } from './utils'
import RefImgsBlock from './RefImgsBlock'
import DrawerFooter from './DrawerFooter'
import GenerateModal from './GenerateModal'
import HistoryPanel from './HistoryPanel'
import MeinianGalleryPanel from './MeinianGalleryPanel'
import './index.less'

const COST_TASK_TYPE = 20
const CLOSE_CONFIRM_Z_INDEX = 10020
const TAB_KEYS = ['free', 'fit', 'background', 'model']

function createEmptyTabDrafts() {
  return TAB_KEYS.reduce((map, key) => ({
    ...map,
    [key]: { refImgs: [], editorState: null, editorContent: '' },
  }), {})
}

function getTemplateText(tab) {
  if (tab === 'free') return ''
  try {
    return (JSON.parse(TEMPLATE_DATA[tab] || '[]') || []).map(item => item.value || '').join('')
  } catch (e) {
    return ''
  }
}

function getImageKey(item) {
  return item?.id ?? item?.url ?? item
}

function createInitialDraft({ refProduct = null, refImgs = [], tab = 'free', editorContent = '' } = {}) {
  return {
    refProduct,
    refImgs,
    editorContentByTab: { [tab]: editorContent },
  }
}

function parseTaskCostCount(res) {
  if (typeof res === 'number' || typeof res === 'string') {
    const count = Number(res)
    return Number.isNaN(count) ? null : count
  }

  const count = Number(res?.costCount ?? res?.taskCostCount ?? res?.count ?? res?.result)
  return Number.isNaN(count) ? null : count
}

const FdAiDrawer = forwardRef(({ showFloatButton = false }, ref) => {
  const [open, setOpen] = useState(false)
  const [tabValue, setTabValue] = useState('free')
  const [refProduct, setRefProduct] = useState(null)
  const [refImgs, setRefImgs] = useState([])
  const [model, setModel] = useState(DEFAULT_MODEL)
  const [ratio, setRatio] = useState(DEFAULT_RATIO)
  const [quality, setQuality] = useState(DEFAULT_QUALITY)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [generateModalOpen, setGenerateModalOpen] = useState(false)
  const [generateData, setGenerateData] = useState(null)
  const [sendLoading, setSendLoading] = useState(false)
  const [refProductUploading, setRefProductUploading] = useState(false)
  const [taskCostCount, setTaskCostCount] = useState(null)
  const [historyList, setHistoryList] = useState([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyPageNo, setHistoryPageNo] = useState(1)
  const [historyHasMore, setHistoryHasMore] = useState(true)
  const [meinianGalleryOpen, setMeinianGalleryOpen] = useState(false)
  const historyLoadingRef = useRef(false)
  const editorRef = useRef(null)
  const taskIdRef = useRef(null)
  const pollingTimerRef = useRef(null)
  const latestCostQueryIdRef = useRef(0)
  const prevOpenRef = useRef(false)
  const closeConfirmRef = useRef(null)
  const initialDraftRef = useRef(createInitialDraft())

  // 停止轮询
  const stopPolling = useCallback(() => {
    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current)
      pollingTimerRef.current = null
    }
  }, [])

  // 开始轮询查询消息状态
  const startPolling = useCallback((conversationId) => {
    const poll = async () => {
      try {
        const msgRes = await apiFetchMessages({ conversationId })
        const messages = msgRes?.resultList || msgRes?.list || []
        const replyMsg = messages.find(m => m.senderType === 2)

        if (!replyMsg?.content) {
          pollingTimerRef.current = setTimeout(poll, 2000)
          return
        }

        const { taskStatus, imagesExplicit, images, taskWorks, progress, text } = replyMsg.content

        if (taskStatus === 'finished' || taskStatus === 'FINISHED') {
          const resultUrl = imagesExplicit?.[0] || images?.[0] || ''
          const workId = taskWorks?.[0]?.workId
          setGenerateData(prev => ({ ...prev, status: 'success', results: resultUrl, workId }))
          stopPolling()
        } else if (taskStatus === 'failed' || taskStatus === 'FAILED') {
          setGenerateData(prev => ({ ...prev, status: 'fail', errorMsg: text || '生成失败' }))
          stopPolling()
        } else {
          if (progress != null) {
            setGenerateData(prev => ({ ...prev, progress }))
          }
          pollingTimerRef.current = setTimeout(poll, 2000)
        }
      } catch (err) {
        console.error('轮询查询失败', err)
        pollingTimerRef.current = setTimeout(poll, 2000)
      }
    }

    poll()
  }, [stopPolling])

  const tabDataRef = useRef(createEmptyTabDrafts())

  const saveCurrentTabDraft = useCallback(() => {
    tabDataRef.current[tabValue] = {
      refImgs: [...refImgs],
      editorState: editorRef.current?.getEditorState() ?? null,
      editorContent: editorRef.current?.getContent?.() || '',
    }
  }, [refImgs, tabValue])

  const resetDraft = useCallback(() => {
    stopPolling()
    setOpen(false)
    setRefProduct(null)
    setRefImgs([])
    setTabValue('free')
    setModel(DEFAULT_MODEL)
    setRatio(DEFAULT_RATIO)
    setQuality(DEFAULT_QUALITY)
    setEditorContent('')
    setHistoryOpen(false)
    setMeinianGalleryOpen(false)
    setGenerateModalOpen(false)
    setGenerateData(null)
    tabDataRef.current = createEmptyTabDrafts()
    initialDraftRef.current = createInitialDraft()
    editorRef.current?.clearContent?.()
  }, [stopPolling])

  const hasDraftChanges = useCallback(() => {
    const currentText = editorRef.current?.getContent?.() || ''
    const draftMap = {
      ...tabDataRef.current,
      [tabValue]: {
        refImgs: [...refImgs],
        editorState: editorRef.current?.getEditorState() ?? null,
        editorContent: currentText,
      },
    }
    const initialRefImgKeys = new Set((initialDraftRef.current.refImgs || []).map(getImageKey))
    const hasChangedRefProduct = Boolean(refProduct && refProduct !== initialDraftRef.current.refProduct)
    const hasAddedRefImg = Object.values(draftMap).some(item => (
      item.refImgs || []
    ).some(img => !initialRefImgKeys.has(getImageKey(img))))
    const initialEditorContentByTab = initialDraftRef.current.editorContentByTab || {}
    const hasCustomText = Object.entries(draftMap).some(([tab, item]) => {
      const content = (item.editorContent || '').trim()
      const initialContent = (initialEditorContentByTab[tab] ?? getTemplateText(tab)).trim()
      return Boolean(content && content !== initialContent)
    })

    return hasChangedRefProduct || hasAddedRefImg || hasCustomText
  }, [refImgs, refProduct, tabValue])

  const handleDrawerClose = useCallback(() => {
    saveCurrentTabDraft()
    if (!hasDraftChanges()) {
      setOpen(false)
      setMeinianGalleryOpen(false)
      return
    }
    if (closeConfirmRef.current) return
    closeConfirmRef.current = Modal.confirm({
      className: 'fdai-close-confirm-modal-mn',
      title: '你已有修改的内容，是否保留？',
      okText: '需要',
      cancelText: '不需要',
      zIndex: CLOSE_CONFIRM_Z_INDEX,
      onOk: () => {
        closeConfirmRef.current = null
        setOpen(false)
        setMeinianGalleryOpen(false)
      },
      onCancel: () => {
        closeConfirmRef.current = null
        resetDraft()
      },
    })
  }, [hasDraftChanges, resetDraft, saveCurrentTabDraft])

  useImperativeHandle(ref, () => ({
    setRefProductUrl: url => {
      setRefProduct(url)
      initialDraftRef.current = createInitialDraft({ refProduct: url || null })
      setOpen(true)
      apiAddUploadPicLog(url).catch(() => {})
    },
  }))

  const handleTabChange = newTab => {
    if (newTab === tabValue) return
    tabDataRef.current[tabValue] = {
      refImgs: [...refImgs],
      editorState: editorRef.current?.getEditorState() ?? null,
      editorContent: editorRef.current?.getContent?.() || '',
    }
    setTabValue(newTab)
  }

  useEffect(() => {
    const saved = tabDataRef.current[tabValue]
    setRefImgs(saved.refImgs)
    if (saved.editorState !== null) {
      editorRef.current?.restoreEditorState(saved.editorState)
      setEditorContent(saved.editorContent || '')
      return
    }
    editorRef.current?.clearContent()
    setEditorContent('')
    if (tabValue === 'free') return
    const templateConfig = TEMPLATE_DATA[tabValue]
    if (templateConfig) {
      try {
        const nodes = JSON.parse(templateConfig || '[]')
        if (nodes.length > 0) generateInsertFunction(nodes, editorRef)()
      } catch (e) { console.warn('templateConfig parse error', e) }
    }
  }, [tabValue])

  useEffect(() => () => { stopPolling() }, [stopPolling])

  // 监听弹窗关闭，停止轮询
  useEffect(() => {
    if (!generateModalOpen) {
      stopPolling()
    }
  }, [generateModalOpen, stopPolling])

  useEffect(() => {
    if (generateData?.status === 'success' && historyOpen) {
      loadHistory(1)
    }
  }, [generateData?.status])

  const handleRefProductSuccess = url => {
    setRefProductUploading(false)
    setRefProduct(url)
    apiAddUploadPicLog(url).catch(() => {})
  }

  const handleRefProductUploadError = err => {
    setRefProductUploading(false)
    message.error(err || '上传失败')
  }

  const handleRefImgSuccess = (url, id) => {
    // 判断 id 是否为文件名：如果包含文件扩展名（如 .jpg, .png 等），则视为文件名
    // 只有当 id 是有效的 ID（数字或纯数字字符串）时，才认为是从列表选择的
    const isFileName = id != null && String(id).includes('.')
    const validId = !isFileName && id != null ? id : null
    const key = validId ?? url
    setRefImgs(prev => {
      if (prev.length >= MAX_REF_IMGS) return prev
      if (prev.some(item => (
        item.url === url || (validId != null && item.id != null && String(item.id) === String(validId)) || (item.id ?? item.url) === key
      ))) return prev
      return [...prev, { id: validId, url }]
    })
    // validId 有值说明是从列表选择的，不需要记录上传日志
    if (validId == null) apiAddUploadPicLog(url).catch(() => {})
  }

  const handleRemoveRefImg = index => setRefImgs(prev => prev.filter((_, i) => i !== index))
  const handleRemoveRefImgBySource = (url, id) => {
    setRefImgs(prev => prev.filter(item => {
      if (item.url === url) return false
      return !(id != null && item.id != null && String(item.id) === String(id))
    }))
  }
  const handleClearRefImgs = () => setRefImgs([])
  const [editorContent, setEditorContent] = useState('')
  const canGenerate = refProduct && (tabValue === 'free' || refImgs.length > 0) && editorContent.trim() && !sendLoading && !refProductUploading

  const updateGenerateParams = useCallback(nextValues => {
    const nextParams = normalizeGenerateParams({
      model,
      quality,
      ratio,
      ...nextValues,
    })
    setModel(nextParams.model)
    setQuality(nextParams.quality)
    setRatio(nextParams.ratio)
  }, [model, quality, ratio])

  const handleModelChange = useCallback(nextModel => {
    updateGenerateParams({ model: nextModel })
  }, [updateGenerateParams])

  const handleQualityChange = useCallback(nextQuality => {
    updateGenerateParams({ quality: nextQuality })
  }, [updateGenerateParams])

  const handleRatioChange = useCallback(nextRatio => {
    updateGenerateParams({ ratio: nextRatio })
  }, [updateGenerateParams])

  const buildImages = () => {
    const urls = refImgs.map(item => item.url)
    return [refProduct, ...urls].filter(Boolean)
  }

  const buildGeneratePayload = useCallback(() => {
    const images = buildImages()
    return {
      content: {
        images,
        text: editorContent.trim(),
        model,
        taskType: tabValue,
      },
      contentType: 2,
      pictureResolution: quality.toUpperCase(),
      pictureAspectRatio: ratio,
      continuousMsg: false,
      messageSource: FDAI_MESSAGE_SOURCE,
    }
  }, [editorContent, model, quality, ratio, refImgs, refProduct, tabValue])

  const buildCostCountPayload = useCallback(() => {
    const normalizedRatio = ratio === 'auto' ? '' : ratio
    const normalizedResolution = quality.toUpperCase()
    const prompt = editorContent.trim()
    const selectedRefImg = refImgs[0]?.url || ''

    if (tabValue === 'fit') {
      const params = {
        styleOnModelPics: refProduct ? [{ originalUrl: refProduct }] : [],
        componentPicUrlList: refImgs.map(item => ({
          componentPicUrl: item.url,
        })),
        replacementMode: {
          type: null,
          replaceImageUrl: '',
        },
        isBigScale: false,
        suiteMode: 'false',
        resolution: normalizedResolution,
        model,
        ratio: normalizedRatio,
        picNum: 1,
      }

      return {
        taskType: COST_TASK_TYPE,
        picNum: 1,
        ratio: normalizedRatio,
        extendInfoData: {
          suiteMode: params.suiteMode,
          replacementMode: params.replacementMode,
          resolution: params.resolution,
          model: params.model,
        },
        extendPicUrl: {
          styleOnModelPics: params.styleOnModelPics,
          componentPicUrlList: params.componentPicUrlList,
          extendInfo: JSON.stringify({ params }),
        },
      }
    }

    if (tabValue === 'background') {
      const params = {
        changeBackgroundType: 1,
        referToOri: true,
        backgroundSimilarityType: 1,
        ratio: normalizedRatio,
        picNum: 1,
        resolution: normalizedResolution,
        model,
        bgInfo: selectedRefImg ? { url: selectedRefImg } : undefined,
      }

      return {
        taskType: COST_TASK_TYPE,
        originalPicUrl: refProduct || '',
        componentPicUrl: selectedRefImg,
        keywords: prompt,
        picNum: 1,
        ratio: normalizedRatio,
        extendInfoData: {
          changeBackgroundType: params.changeBackgroundType,
          referToOri: params.referToOri,
          backgroundSimilarityType: params.backgroundSimilarityType,
          resolution: params.resolution,
          model: params.model,
        },
        extendPicUrl: {
          extendInfo: JSON.stringify({ params }),
        },
      }
    }

    if (tabValue === 'model') {
      const params = {
        picNum: 1,
        ratio: normalizedRatio,
        resolution: normalizedResolution,
        model,
        replacementMode: {
          type: null,
          replaceImageUrl: '',
        },
      }

      return {
        taskType: COST_TASK_TYPE,
        originalPicUrl: refProduct || '',
        componentPicUrl: selectedRefImg,
        keywords: prompt,
        picNum: 1,
        ratio: normalizedRatio,
        extendInfoData: {
          resolution: params.resolution,
          model: params.model,
          replacementMode: params.replacementMode,
        },
        extendPicUrl: {
          extendInfo: JSON.stringify({ params }),
        },
      }
    }

    const params = {
      picNum: 1,
      ratio: normalizedRatio,
      resolution: normalizedResolution,
      model,
      partialModificationRefImages: refImgs.map(item => ({
        originalUrl: item.url,
      })),
    }

    return {
      taskType: COST_TASK_TYPE,
      originalPicUrl: refProduct || '',
      keywords: prompt,
      picNum: 1,
      ratio: normalizedRatio,
      extendInfoData: {
        resolution: normalizedResolution,
        model,
      },
      extendPicUrl: {
        partialModificationRefImages: params.partialModificationRefImages,
        extendInfo: JSON.stringify({ params }),
      },
    }
  }, [editorContent, model, quality, ratio, refImgs, refProduct, tabValue])

  const fetchTaskCostCount = useCallback(async payload => {
    const currentQueryId = latestCostQueryIdRef.current + 1
    latestCostQueryIdRef.current = currentQueryId
    try {
      const res = await apiGetTaskCostCount(payload)
      if (latestCostQueryIdRef.current !== currentQueryId) return
      setTaskCostCount(parseTaskCostCount(res))
    } catch (error) {
      if (latestCostQueryIdRef.current === currentQueryId) {
        setTaskCostCount(null)
      }
    }
  }, [])

  const costCountPayload = buildCostCountPayload()
  const costCountPayloadKey = JSON.stringify(costCountPayload)

  useEffect(() => {
    if (!open) {
      prevOpenRef.current = false
      setTaskCostCount(null)
      return
    }

    const isOpening = !prevOpenRef.current
    prevOpenRef.current = true

    if (isOpening) {
      fetchTaskCostCount(costCountPayload)
      return
    }

    const timer = setTimeout(() => {
      fetchTaskCostCount(costCountPayload)
    }, 300)

    return () => {
      clearTimeout(timer)
    }
  }, [open, costCountPayload, costCountPayloadKey, fetchTaskCostCount])

  const handleGenerate = async () => {
    if (!canGenerate) return
    const tabLabel = TAB_OPTIONS.find(t => t.value === tabValue)?.label || '自由改款'
    const prompt = editorContent.trim()
    const generatePayload = buildGeneratePayload()
    zyTrack.reportCustom({
      event: 'fd_ai_generate_click',
      module1: 'FD+智能改款',
      module2: '生成图片',
      desc: 'FD+智能改款_生成图片',
      generateType: tabValue,
      generateTypeLabel: tabLabel,
      refProduct,
      refImgs: refImgs.map(item => item.url).join(','),
      prompt,
    })
    setSendLoading(true)
    let conversationId
    try {
      // 先创建对话
      const createRes = await apiCreateConversation({
        pictureResolution: quality.toUpperCase(),
        pictureAspectRatio: ratio,
        continuousMsg: false,
        messageSource: FDAI_MESSAGE_SOURCE,
      })
      conversationId = createRes?.conversationId

      // 再发送消息
      await apiSendMessage(generatePayload, conversationId)

      // 查询消息列表，判断回复消息是否有 taskId
      const msgRes = await apiFetchMessages({ conversationId })
      const messages = msgRes?.resultList || msgRes?.list || []
      const replyMsg = messages.find(m => m.senderType === 2)
      if (!replyMsg?.content?.taskId) {
        message.error(replyMsg?.content?.text || '生成失败，请重试')
        return
      }

      const taskId = conversationId
      setGenerateData({
        status: 'generating',
        progress: 0,
        taskId,
        workId: null,
        results: '',
        refProduct,
        refImgs: refImgs.map(item => item.url),
        tabLabel,
        desc: prompt,
      })
      setGenerateModalOpen(true)
      if (taskId) {
        taskIdRef.current = taskId
        startPolling(taskId)
      }
    } catch (err) {
      console.error('生成失败', err)
      if (conversationId) apiDeleteConversation(conversationId).catch(() => {})
      message.error('生成失败，请重试')
    } finally {
      setSendLoading(false)
    }
  }

  const PAGE_SIZE = 20

  const loadHistory = useCallback((pageNo = 1) => {
    if (historyLoadingRef.current) return
    historyLoadingRef.current = true
    setHistoryLoading(true)
    apiFetchHistory({ messageSource: FDAI_MESSAGE_SOURCE, pageNo, pageSize: PAGE_SIZE })
      .then(res => {
        const list = res?.resultList || []
        setHistoryList(prev => (pageNo === 1 ? list : [...prev, ...list]).filter(item => item.topMessageDTOList))
        setHistoryPageNo(pageNo)
        setHistoryHasMore(list.length === PAGE_SIZE)
      })
      .catch(() => { if (pageNo === 1) setHistoryList([]) })
      .finally(() => {
        historyLoadingRef.current = false
        setHistoryLoading(false)
      })
  }, [])

  const handleLoadMoreHistory = useCallback(() => {
    if (!historyHasMore || historyLoadingRef.current) return
    loadHistory(historyPageNo + 1)
  }, [historyHasMore, historyPageNo, loadHistory])

  const handleOpenHistory = () => {
    setHistoryOpen(true)
    setHistoryPageNo(1)
    setHistoryHasMore(true)
    loadHistory(1)
  }

  const handleDownload = async (results, workId) => {
    if (workId) {
      try {
        const blob = await apiSingleDownload(workId)
        downloadBlob(blob)
      } catch {
        downloadOssImageByUrl(results)
      }
    } else {
      downloadOssImageByUrl(results)
    }
  }

  const handleRegenerate = () => {
    stopPolling()
    setGenerateModalOpen(false)
  }

  const handleHistoryRegenerate = async item => {
    // 先保存当前 tab 的数据
    tabDataRef.current[tabValue] = {
      refImgs: [...refImgs],
      editorState: editorRef.current?.getEditorState() ?? null,
      editorContent: editorRef.current?.getContent?.() || '',
    }

    // 设置参考商品
    setRefProduct(item.refProduct || null)

    // 准备目标 tab 的数据
    const targetTab = item.taskType || 'free'
    const targetRefImgs = (item.refImgs || []).map(url => ({ id: null, url }))
    initialDraftRef.current = createInitialDraft({
      refProduct: item.refProduct || null,
      refImgs: targetRefImgs,
      tab: targetTab,
      editorContent: item.desc || '',
    })

    // 先设置编辑器内容
    editorRef.current?.clearContent()
    if (item.desc) editorRef.current?.insertText?.(item.desc)
    setEditorContent(item.desc || '')

    // 使用 setTimeout 确保编辑器状态更新后再保存和切换 tab
    setTimeout(() => {
      // 将编辑器状态保存到目标 tab，这样切换 tab 时不会触发模板加载
      tabDataRef.current[targetTab] = {
        refImgs: targetRefImgs,
        editorState: editorRef.current?.getEditorState() ?? null,
        editorContent: item.desc || '',
      }

      // 直接回填参考图，避免目标 tab 与当前 tab 相同时 setTabValue 不触发恢复逻辑
      setRefImgs(targetRefImgs)

      // 切换到目标 tab（这会触发 useEffect，但因为 editorState 不为 null，所以会恢复我们设置的内容）
      setTabValue(targetTab)
    }, 0)

    // 获取对话参数
    if (item.id) {
      try {
        const conversation = await apiConversationGet({ conversationId: item.id })
        const messages = await apiFetchMessages({ conversationId: item.id })
        const sendMsg = (messages?.resultList || messages?.list || []).find(m => m.senderType === 1)
        const sendContent = sendMsg?.content || {}

        updateGenerateParams({
          model: sendContent.model || model,
          quality: conversation.pictureResolution?.toLowerCase() || quality,
          ratio: conversation.pictureAspectRatio || ratio,
        })
      } catch (err) {
        console.error('获取对话参数失败', err)
      }
    }
  }

  const handleViewGenerating = item => {
    setGenerateData({
      status: 'generating',
      progress: item.progress || 0,
      taskId: item.id,
      workId: item.workId,
      results: item.results || '',
      refProduct: item.refProduct,
      refImgs: item.refImgs,
      tabLabel: item.tabLabel,
      desc: item.desc,
    })
    setGenerateModalOpen(true)
    if (item.id) {
      taskIdRef.current = item.id
      startPolling(item.id)
    }
  }

  const handleContinue = () => {
    if (generateData?.results) setRefProduct(generateData.results)
    stopPolling()
    setGenerateModalOpen(false)
  }

  const renderHistoryItem = item => {
    const { topMessageDTOList = [], createdAt, title, continuousMsg } = item
    const sendMsg = topMessageDTOList.find(m => m.senderType === 1)
    const replyMsg = topMessageDTOList.find(m => m.senderType === 2)
    const sendContent = sendMsg?.content || {}
    const replyContent = replyMsg?.content || {}
    const images = sendContent.images || []
    const { taskStatus } = replyContent
    let status
    if (taskStatus === 'FINISHED' || taskStatus === 'finished') status = 'success'
    else if (taskStatus === 'FAILED' || taskStatus === 'failed') status = 'fail'
    else status = 'generating'
    return {
      id: topMessageDTOList[0].conversationId,
      status,
      time: sendMsg?.createdAt || createdAt || '',
      desc: sendContent.text || '',
      refProduct: images[0] || '',
      refImgs: images.slice(1),
      taskType: sendContent.taskType || '',
      tabLabel: TAB_OPTIONS.find(t => t.value === sendContent.taskType)?.label || title || '',
      results: replyContent.imagesExplicit?.[0] || replyContent.images?.[0] || '',
      workId: replyContent.taskWorks?.[0]?.workId,
      progress: replyContent.progress,
      errorMsg: replyContent.text,
      continuousMsg: continuousMsg ?? false,
    }
  }

  const historyItems = historyList.map(renderHistoryItem)

  return (
    <>
      {showFloatButton && (
        <button
          type="button"
          className="fdai-float-btn"
          onClick={() => {
            zyTrack.reportCustom({
              event: 'fd_ai_float_click',
              module1: 'FD+智能改款',
              module2: '悬浮入口',
              desc: 'FD+智能改款_悬浮入口点击',
            })
            setOpen(true)
          }}
          aria-label="Open FdAi drawer"
          title="打开改款抽屉"
        >
          <img src="https://cdn.zhiyitech.cn/material/3c3d74049f350f7322c9783564b0799e" alt="fdai" />
        </button>
      )}

      <aside className={`fdai-drawer ${open ? 'open' : ''}`} aria-label="fdai-drawer">
        <MeinianGalleryPanel
          open={open && meinianGalleryOpen}
          refImgs={refImgs}
          onSelect={handleRefImgSuccess}
          onUnselect={handleRemoveRefImgBySource}
          onClose={() => setMeinianGalleryOpen(false)}
        />

        <div className="fdai-drawer-header">
          <a className="left" href="/plus-fd/" target="_blank" rel="noopener noreferrer">
            <img src="https://cdn.zhiyitech.cn/material/ca54a3300606e910dc35fd67a81018d0" alt="fdai-logo" />
          </a>
          <div className="right">
            <button type="button" className="history-btn" onClick={handleOpenHistory}>
              <Icon className="tips" type="icon-lishijilu" />
              历史记录
            </button>
            <button type="button" className="fdai-drawer-close" onClick={handleDrawerClose} aria-label="Close">
              <Icon className="tips" type="zykj-guanbi" />
            </button>
          </div>
        </div>

        <div className="fdai-drawer-body">
          <div className="ref-block">
            <div className="ref-title">参考商品</div>
            <div className="ref-main">
              <UploadImg
                draggable
                maxFileSize={25}
                onStart={() => setRefProductUploading(true)}
                onSuccess={handleRefProductSuccess}
                onError={handleRefProductUploadError}
                className="ref-product-upload"
              >
                {refProduct ? (
                  <div className="ref-product-img-wrap">
                    <img src={refProduct} alt="reference" />
                    <div className="ref-product-label">{IMG_LABELS[0]}</div>
                    <button type="button" className="ref-product-delete" onClick={e => { e.stopPropagation(); setRefProduct(null) }}>
                      <Icon type="zykj-guanbi" style={{ fontSize: '10px' }} />
                    </button>
                  </div>
                ) : (
                  <div className="ref-product-placeholder">
                    {refProductUploading ? (
                      <>
                        <span className="fdai-upload-spinner" />
                        <span>上传中...</span>
                      </>
                    ) : (
                      <>
                        <Icon type="zykj-jiahao" />
                        <span>点击/拖拽上传</span>
                      </>
                    )}
                  </div>
                )}
              </UploadImg>
            </div>
          </div>

          <div className="fdai-tabs">
            {TAB_OPTIONS.map(opt => (
              <button type="button" key={opt.value} className={`fdai-tab ${tabValue === opt.value ? 'active' : ''}`} onClick={() => handleTabChange(opt.value)}>
                <span className="fdai-tab-text">{opt.label}</span>
              </button>
            ))}
          </div>

          <div className="ref-imgs-desc-container">
            <div className="fdai-desc-block">
              <div className="fdai-desc-label">改款描述</div>
              <div className="fdai-structured-editor">
                <StructuredDescEditor ref={editorRef} placeholder="请输入改款描述" onChange={setEditorContent} />
              </div>
            </div>
            <RefImgsBlock
              enabled={open}
              refImgs={refImgs}
              onAdd={handleRefImgSuccess}
              onRemove={handleRemoveRefImg}
              onClear={handleClearRefImgs}
              onOpenMeinianGallery={() => setMeinianGalleryOpen(true)}
            />
          </div>
        </div>

        <HistoryPanel
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          items={historyItems}
          loading={historyLoading}
          hasMore={historyHasMore}
          onLoadMore={handleLoadMoreHistory}
          onDownload={handleDownload}
          onRegenerate={handleHistoryRegenerate}
          onViewGenerating={handleViewGenerating}
        />

        <DrawerFooter
          model={model}
          ratio={ratio}
          quality={quality}
          count={taskCostCount}
          onModelChange={handleModelChange}
          onRatioChange={handleRatioChange}
          onQualityChange={handleQualityChange}
          canGenerate={canGenerate}
          loading={sendLoading}
          onGenerate={handleGenerate}
        />

        <GenerateModal
          open={generateModalOpen}
          data={generateData}
          onClose={() => setGenerateModalOpen(false)}
          onRegenerate={handleRegenerate}
          onContinue={handleContinue}
          onDownload={handleDownload}
        />
      </aside>
    </>
  )
})

FdAiDrawer.displayName = 'FdAiDrawer'

export default FdAiDrawer
